import { useSelector } from 'react-redux';
import { StyledDealSetupFooter } from './DealSetupScreen.style';
import { Button } from '@makemydeal/dr-ui-bricks-components';
import { getVehicle, useSellingVehicleSelector, type SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';
import { useDealSetup, useValidateVehicle } from '../../hooks';
import type { DealSetupFooterProps } from '../../types';

/**
 * DealSetupFooter — Footer with Launch Deal button for the Deal Setup screen.
 *
 * Launch Deal Button Validation:
 *  - Dealership is ALWAYS required
 *  - When Deal Setup must be completed: a valid vehicle AND a complete customer are required
 *  - When it can be skipped: customer and vehicle are optional (only dealership needed)
 */
export const DealSetupFooter: React.FC<DealSetupFooterProps & { isCustomerReady?: boolean }> = ({
    onLaunch,
    isLaunchInProgress,
    isCustomerReady = false
}) => {
    const { hasDealerSettings, isDealSetupRequired } = useDealSetup();

    // Vehicle from the change-vehicle UI (newly selected). Inline actions apply the row selection to
    // `state.salesCheckin.vehicle` immediately, so `getVehicle` is current.
    const selectedVehicle = useSelector((state: SalesCheckinRootState) => getVehicle(state));
    const isNewVehicleValid = useValidateVehicle(selectedVehicle);

    // Vehicle already on the deal (from bootstrap)
    const dealVehicle = useSellingVehicleSelector();
    const hasDealVehicle = !!dealVehicle.vin;

    // Vehicle is valid if user selected a new valid one OR deal already has one
    const hasValidVehicle = isNewVehicleValid || hasDealVehicle;

    // Launch Deal button validation. isCustomerReady is true when the card has unsaved-but-valid
    // edits (canApply) or the deal already has a complete customer (hasValidCustomer). Dealers who
    // can skip Deal Setup need nothing beyond the dealership, so they fall straight through.
    //
    // The hasDealerSettings guard is load-bearing: capabilities are fail-closed, so before dealer
    // settings land isDealSetupRequired reads false, which would otherwise look like a dealer
    // who may skip setup and enable Launch Deal prematurely.
    const isDealSetupComplete = hasValidVehicle && isCustomerReady;
    const isLaunchDealDisabled = !hasDealerSettings || (isDealSetupRequired && !isDealSetupComplete);

    return (
        <StyledDealSetupFooter data-testid="deal-setup-footer">
            <Button
                buttonStyle="primary"
                onClick={onLaunch}
                disabled={isLaunchDealDisabled || isLaunchInProgress}
                data-testid="deal-setup-launch-deal-button"
            >
                Launch Deal
            </Button>
        </StyledDealSetupFooter>
    );
};
